import React, { useRef } from 'react';

import { useFormContext } from 'react-hook-form';

import { Add } from '@mui/icons-material';
import {
    Button,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import type RefundForm from '../../models/RefundForm';
import type RefundLineItem from '../../models/RefundLineItem';

import AddProductsDialog, {
    AddProductsDialogHandle,
} from './AddProductsDialog';

import IfCondition from 'components/shared/IfCondition';
import TextFieldControlled from 'components/shared/TextFieldControlled';
import { useTranslation } from 'react-i18next';
import { RefundItemsTable } from './RefundItemsTable';
import SelectRefundReason from './SelectRefundReason';

const ProductsStep: React.FC = () => {
    const { t } = useTranslation('refund', { keyPrefix: 'PRODUCTS_STEP' });

    const theme = useTheme();

    const small = useMediaQuery(theme.breakpoints.down('md'));

    const form = useFormContext<RefundForm>();

    const dialogRef = useRef<AddProductsDialogHandle>(null);

    const handleSubmit = (items: RefundLineItem[]) => {
        form.setValue('products', items);
        form.trigger('products');
        dialogRef.current?.close();
    };

    const isOtherReason = form.watch('reason.is_other_reason');

    const productsError = form.formState?.errors?.['products']?.message;

    return (
        <Stack gap={2}>
            <AddProductsDialog onSubmit={handleSubmit} ref={dialogRef} />

            <RefundItemsTable />

            <IfCondition condition={!!productsError}>
                <Typography color="error" variant="caption">
                    {productsError}
                </Typography>
            </IfCondition>

            <Button
                variant="outlined"
                color="primary"
                startIcon={<Add />}
                sx={{ maxWidth: small ? 'auto' : 'fit-content' }}
                onClick={() => dialogRef.current?.open()}
            >
                {t('ADD_PRODUCT')}
            </Button>

            <Typography variant="caption">{t('REFUND_REASON')}</Typography>

            <Stack gap={2}>
                <SelectRefundReason />

                <IfCondition condition={isOtherReason}>
                    <TextFieldControlled
                        label={t('OTHER_REASON')}
                        control={form.control}
                        name="reason.text"
                    />
                </IfCondition>
            </Stack>
        </Stack>
    );
};

export default ProductsStep;
